'use client';

function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useState } from 'react';
import { TextField } from '../TextField';
import { getCardTypeByValue, PLACEHOLDER } from './cardTypes';
import { Container, NeedHelpLink } from './elements';
import { processCreditCardNumber } from './util';
export const CreditCardField = /*#__PURE__*/React.forwardRef(({
  needHelpHref,
  needHelpLabel,
  affixRight = null,
  disabled = false,
  affixRightClickable,
  label,
  fieldStatus,
  helperText,
  value,
  onChange,
  containerClassName = 'CreditCardFieldContainer',
  ...restProps
}, fRef) => {
  const needHelpHrefPresent = !!needHelpHref;

  //This is being used to store the card number that is being shown to user
  const [cardFieldValue, setCardFieldValue] = useState('');

  //Used to store the real card number
  const [cardNumber, setCardNumber] = useState((value ?? '').toString());
  const [length, setLength] = useState({
    min: 16,
    max: 16
  });
  const [cardType, setCardType] = useState(PLACEHOLDER);
  const [isOnFocus, setIsOnFocus] = useState(false);
  const [status, setStatus] = useState(fieldStatus);
  const [message, setMessage] = useState(helperText);
  const updateCardDetails = (paymentCardType = cardType, ccNumber = cardNumber, isOnFocus = true) => {
    const processResult = processCreditCardNumber(paymentCardType, ccNumber, !isOnFocus);
    setCardNumber(processResult.cardNumber);
    setCardFieldValue(processResult.creditCardFieldValue);
    if (isOnFocus) {
      //Generic Card error should be shown even on focus
      if (cardType.type === 'generic' && processResult.cardNumber.length > 4) {
        setStatus('error');
        setMessage(processResult.errorMessage);
      } else {
        setStatus('default');
        setMessage('');
      }
    } else {
      if (processResult.errorMessage && processResult.cardNumber.length > 0 && (!helperText || helperText.length === 0)) {
        setStatus('error');
        setMessage(processResult.errorMessage);
      } else {
        setStatus(fieldStatus);
        setMessage(helperText);
      }
    }
    return processResult;
  };
  const onCardNumberChange = ev => {
    const {
      value
    } = ev.target;
    const cardTypeObject = getCardTypeByValue(value || '');
    setCardType(cardTypeObject);
    const {
      cardNumber,
      errorMessage
    } = updateCardDetails(cardTypeObject, value);
    Object.assign(ev.target, {
      cardType: cardTypeObject.type,
      value: cardNumber,
      errorMessage
    });
    onChange === null || onChange === void 0 || onChange(ev);
  };
  const setInputLength = () => {
    //This variable is used to include the separators while calculating field length
    const gaps = cardType.gaps.length;
    setLength({
      min: cardType.lengths[0] + gaps,
      max: cardType.lengths[cardType.lengths.length - 1] + gaps
    });
  };
  useEffect(() => {
    setStatus(fieldStatus);
  }, [fieldStatus]);
  useEffect(() => {
    setMessage(helperText);
  }, [helperText]);
  useEffect(() => {
    updateCardDetails(cardType, value === null || value === void 0 ? void 0 : value.toString(), isOnFocus);
  }, [value]);
  useEffect(() => {
    updateCardDetails(cardType, cardNumber, isOnFocus);
  }, [isOnFocus]);
  useEffect(() => {
    setInputLength();
  }, [cardType]);
  return /*#__PURE__*/React.createElement(Container, {
    "data-aui": "credit-card-field",
    $disabled: disabled
  }, /*#__PURE__*/React.createElement(TextField, _extends({}, restProps, {
    value: cardFieldValue,
    containerClassName: containerClassName,
    ref: fRef,
    type: 'text',
    affixRight: affixRight,
    disabled: disabled,
    affixRightClickable: affixRightClickable,
    inputMode: "numeric",
    maxLength: length.max,
    minLength: length.min,
    label: label,
    affixPaymentBadge: cardType.type,
    onChange: ev => {
      onCardNumberChange(ev);
    },
    onFocus: () => {
      setIsOnFocus(true);
    },
    onBlur: () => {
      setIsOnFocus(false);
    },
    fieldStatus: status,
    helperText: message,
    affixRightIconClassName: 'CreditCardRightIcon',
    affixPaymentBadgeAltText: cardType.displayName
  })), needHelpHrefPresent && /*#__PURE__*/React.createElement(NeedHelpLink, {
    href: needHelpHref,
    color: "secondary",
    disabled: disabled,
    className: disabled ? 'disabled' : ''
  }, needHelpLabel ?? 'Need Help?'));
});